import type { Vendor } from '@urbanx/agx.campaigns.client';
import React, { useState, useCallback, useContext } from 'react';
import { PropertyAddress, PropertyDetails, PropertyImageUrls } from '@urbanx/agx-ui-components';
import { CampaignsApiContext } from '@urbanx/agx.campaigns.client';
import { useAppSelector } from 'hooks/useAppSelector';
import { useAppDispatch } from 'hooks/useAppDispatch';
import { useToaster } from 'hooks/useToaster';
import { beginCampaign } from 'Api/Campaigns/campaignsApi';
import PropertySearch from './PropertySearch/PropertySearch';
import ManualAddress from './ManualAddress/ManualAddress';
import './BeginCampaign.scss';

interface BeginCampaignProps {
  id: string;
}

enum BeginCampaignSteps {
  AddressSearchForm = 'AddressSearchForm',
  ManualAddressForm = 'ManualAddressForm',
}

const BeginCampaign: React.FC<BeginCampaignProps> = ({ id }) => {
  const [step, setStep] = useState(BeginCampaignSteps.AddressSearchForm);
  const [disableBeginCampaignButtons, setDisableBeginCampaignButtons] = useState(false);

  const campaigns = useContext(CampaignsApiContext);
  const { showErrorToast } = useToaster();

  const dispatch = useAppDispatch();

  const { formType, state } = useAppSelector((state) => state.form.selectedForm) || {};

  const beginPropertyCampaign = useCallback(
    async (
      isManualAddress: boolean,
      propertyAddress: PropertyAddress,
      propertyImageUrls: PropertyImageUrls | undefined = undefined,
      propertyDetails: PropertyDetails | undefined = undefined,
      vendors: Vendor[] | undefined = undefined,
      hasPool: boolean = false
    ) => {
      if (disableBeginCampaignButtons) return;

      setDisableBeginCampaignButtons(true);
      try {
        if (!formType || !state) throw new Error('Select a form type and property state.');
        await beginCampaign(
          campaigns,
          dispatch,
          propertyAddress,
          formType,
          state,
          isManualAddress,
          propertyImageUrls,
          propertyDetails,
          vendors,
          hasPool
        );
      } catch (error) {
        showErrorToast(error instanceof Error ? error.message : 'Failed to start campaign.');
      } finally {
        setDisableBeginCampaignButtons(false);
      }
    },
    [disableBeginCampaignButtons, campaigns, formType, state, dispatch, showErrorToast]
  );

  let beginCampaignBody = null;
  if (step === BeginCampaignSteps.AddressSearchForm)
    beginCampaignBody = (
      <PropertySearch
        id={id}
        onManualAddress={() => setStep(BeginCampaignSteps.ManualAddressForm)}
        onBeginCampaign={(address, imageUrls, propertyDetails, vendors, hasPool) =>
          beginPropertyCampaign(false, address, imageUrls, propertyDetails, vendors, hasPool)
        }
        disabled={disableBeginCampaignButtons}
      />
    );

  if (step === BeginCampaignSteps.ManualAddressForm) {
    beginCampaignBody = (
      <ManualAddress
        disabled={disableBeginCampaignButtons}
        onAddressSearch={() => setStep(BeginCampaignSteps.AddressSearchForm)}
        onBeginCampaign={(address) => beginPropertyCampaign(true, address)}
      />
    );
  }

  return <div className="agx-noForm inverted">{beginCampaignBody}</div>;
};

export default BeginCampaign;
